import { useRef } from 'react';
import { useSocketEvent } from '@connect-x-app/hybrid-data-access';
import {
  AuthenticatedUser,
  QueryKeys,
  UserPermissionUpdateEvent,
  UserStatus,
  UserStatusUpdateEvent,
} from '@connect-x-app/shared-domain-models';
import { useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';

import { useAuthenticationStore, useSocketStore } from '../../stores';

type Props = {
  onOpenAlert: (closeAlert: () => void) => void;
};

export const useAuthSocketEvents = ({ onOpenAlert }: Props) => {
  const { setUser, user } = useAuthenticationStore();
  const { socket } = useSocketStore();

  const queryClient = useQueryClient();

  // A ref, not state: the backend can send `user.permission.update` and `auth.update` for one change, back to back,
  // and the second must see the alert as open before React has rendered again.
  const isPermissionAlertOpen = useRef(false);

  const handleAuthUpdate = (updatedUser: AuthenticatedUser) => {
    setUser(updatedUser);

    const permissionsDidChange = user && !isEqual(user.permissions, updatedUser.permissions);
    if (permissionsDidChange) {
      handlePermissionUpdate({ externalId: updatedUser.externalId, permissions: updatedUser.permissions }, false);
    }
  };

  const handlePermissionUpdate = (updateEvent: UserPermissionUpdateEvent, updateState = true) => {
    const { externalId, permissions } = updateEvent;

    if (!user) {
      return;
    }

    if (externalId !== user.externalId) {
      return;
    }

    if (!Array.isArray(permissions)) {
      return;
    }

    if (updateState) {
      setUser({ ...user, permissions });
    }

    if (!isPermissionAlertOpen.current) {
      isPermissionAlertOpen.current = true;

      const closeAlert = () => {
        isPermissionAlertOpen.current = false;
      };
      onOpenAlert(closeAlert);
    }

    queryClient.resetQueries({ queryKey: [QueryKeys.FLIGHTROOMS] });
  };

  const handleUserStatusUpdate = (userUpdate: UserStatusUpdateEvent) => {
    if (userUpdate.externalId === user?.externalId && userUpdate.userStatus === UserStatus.INACTIVE) {
      queryClient.resetQueries();
    }
  };

  useSocketEvent(socket, 'auth.update', handleAuthUpdate);
  // These socket updates are actually 'auth' updates, since these are related specifically to your users. This is the
  // reason why it is kept inside this socket event handler and not in the useUserSocketEvents hook.
  useSocketEvent(socket, 'user.permission.update', handlePermissionUpdate);
  useSocketEvent(socket, 'user.status.update', handleUserStatusUpdate);
};
